import { useEffect, useState } from "react";
import { Plus, QrCode, Trash2 } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import SearchableSelect from "../../components/SearchableSelect";
import DocumentScannerBox from "../../components/DocumentScannerBox";

export default function Bookings() {
  const [list, setList] = useState([]);
  const [cars, setCars] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ car_id: "", customer_id: "", data_inizio: "", data_fine: "", extra: [], coupon: "" });

  const load = async () => {
    const [b, c, cu] = await Promise.all([api.get("/bookings"), api.get("/cars"), api.get("/customers")]);
    setList(b.data); setCars(c.data); setCustomers(cu.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/bookings", form);
      toast.success(`Prenotazione creata · Totale €${r.data.totale}`);
      setShow(false);
      setForm({ car_id: "", customer_id: "", data_inizio: "", data_fine: "", extra: [], coupon: "" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const cancel = async (id) => {
    if (!window.confirm("Annullare la prenotazione?")) return;
    await api.delete(`/bookings/${id}`);
    toast.success("Annullata"); load();
  };

  return (
    <div className="space-y-6" data-testid="bookings-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-display text-3xl font-black uppercase tracking-tight">Prenotazioni</h1>
          <p className="text-sm text-white/45 mt-1">{list.length} prenotazioni totali</p>
        </div>
        <button data-testid="booking-add" onClick={() => setShow(true)} className="btn-neon px-5 py-2 rounded-full text-xs inline-flex items-center gap-2">
          <Plus size={14} /> Nuova Prenotazione
        </button>
      </div>

      <div className="glass rounded-2xl overflow-x-auto -mx-3 sm:mx-0 px-3 sm:px-0">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-white/[0.03] text-[10px] uppercase tracking-widest text-white/40">
            <tr>
              <th className="text-left p-4">Auto</th><th className="text-left p-4">Cliente</th>
              <th className="p-4">Dal</th><th className="p-4">Al</th><th className="p-4">Giorni</th>
              <th className="p-4 text-right">Totale</th><th className="p-4">QR</th><th className="p-4"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {list.map((b) => (
              <tr key={b.id} className="hover:bg-white/[0.02]" data-testid={`booking-${b.id}`}>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    {b.car?.foto?.[0] && <img src={b.car.foto[0]} alt="" className="w-12 h-9 rounded object-cover" />}
                    <div>
                      <div className="font-medium">{b.car?.marca} {b.car?.modello}</div>
                      <div className="text-xs text-white/40 font-mono">{b.car?.targa}</div>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <div className="font-medium">{b.customer?.nome} {b.customer?.cognome}</div>
                  <div className="text-xs text-white/40">{b.customer?.email}</div>
                </td>
                <td className="p-4 text-center font-mono text-xs">{b.data_inizio}</td>
                <td className="p-4 text-center font-mono text-xs">{b.data_fine}</td>
                <td className="p-4 text-center font-display font-bold">{b.giorni}</td>
                <td className="p-4 text-right">
                  <div className="font-display font-extrabold neon-text">€{b.totale}</div>
                </td>
                <td className="p-4 text-center">
                  <div className="inline-flex items-center gap-1 text-[10px] font-mono text-white/60 px-2 py-1 rounded bg-white/5">
                    <QrCode size={11} />{b.qr_code}
                  </div>
                </td>
                <td className="p-4 text-right">
                  <button onClick={() => cancel(b.id)} className="p-2 rounded-lg hover:bg-[#ff0033]/15 text-white/50 hover:text-[#ff0033]">
                    <Trash2 size={13} />
                  </button>
                </td>
              </tr>
            ))}
            {list.length === 0 && (
              <tr><td colSpan="8" className="p-10 text-center text-white/40">Nessuna prenotazione. Creane una nuova.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {show && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-6" onClick={() => setShow(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit}
            className="w-full max-w-lg glass-strong rounded-3xl p-7 max-h-[92vh] overflow-auto" data-testid="booking-form">
            <div className="font-display text-2xl font-bold mb-5">Nuova Prenotazione</div>

            {/* AI Scanner */}
            <div className="mb-4">
              <DocumentScannerBox
                testIdPrefix="booking-scanner"
                context="bookings"
                variant="full"
                title="Scansione AI · Voucher / Documento cliente"
                description="Carica voucher prenotazione, patente o documento cliente: l'AI riconosce il cliente in anagrafica e propone date noleggio."
                onResult={(d) => {
                  const persona = d?.persona || {};
                  const noleggio = d?.noleggio || {};
                  const v = d?.veicolo || {};
                  const cf = (persona.codice_fiscale || persona.piva || "").toUpperCase().trim();
                  let customer_id = form.customer_id;
                  if (cf) {
                    const m = customers.find((c) =>
                      (c.codice_fiscale || "").toUpperCase() === cf || (c.piva || "").toUpperCase() === cf
                    );
                    if (m) customer_id = m.id;
                  }
                  let car_id = form.car_id;
                  if (v.targa) {
                    const t = String(v.targa).toUpperCase().replace(/[\s-]/g, '');
                    const mc = cars.find((c) => (c.targa || "").toUpperCase().replace(/[\s-]/g, '') === t);
                    if (mc) car_id = mc.id;
                  }
                  setForm((f) => ({
                    ...f,
                    customer_id,
                    car_id,
                    data_inizio: (noleggio.data_inizio || "").slice(0, 10) || f.data_inizio,
                    data_fine: (noleggio.data_fine || "").slice(0, 10) || f.data_fine,
                  }));
                }}
              />
            </div>

            <div className="space-y-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40">Auto</label>
                <SearchableSelect
                  testid="booking-car"
                  value={form.car_id}
                  onChange={(v) => setForm({ ...form, car_id: v })}
                  placeholder="Cerca marca, modello, targa…"
                  options={cars.filter((c) => c.stato === "disponibile").map((c) => ({
                    value: c.id,
                    label: `${c.marca} ${c.modello} · ${c.targa}`,
                    extra: `€${c.prezzo_giornaliero}/giorno`,
                  }))}
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40">Cliente</label>
                <SearchableSelect
                  testid="booking-cust"
                  value={form.customer_id}
                  onChange={(v) => setForm({ ...form, customer_id: v })}
                  placeholder="Cerca nome, cognome, email, CF…"
                  options={customers.filter((c) => !c.blacklist).map((c) => ({
                    value: c.id,
                    label: c.ragione_sociale || `${c.nome || ""} ${c.cognome || ""}`.trim() || "(senza nome)",
                    extra: c.codice_fiscale || c.piva || c.email || "",
                  }))}
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-white/40">Dal</label>
                  <input required type="date" value={form.data_inizio} onChange={(e) => setForm({ ...form, data_inizio: e.target.value })}
                    className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-white/40">Al</label>
                  <input required type="date" value={form.data_fine} onChange={(e) => setForm({ ...form, data_fine: e.target.value })}
                    className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40">Coupon (FABRI10 = -10%)</label>
                <input value={form.coupon} onChange={(e) => setForm({ ...form, coupon: e.target.value })}
                  className="mt-1 w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm" placeholder="Opzionale" />
              </div>
            </div>
            <div className="flex gap-3 mt-6 justify-end">
              <button type="button" onClick={() => setShow(false)} className="px-5 py-2.5 rounded-full text-sm bg-white/[0.04] border border-white/10">Annulla</button>
              <button data-testid="booking-form-submit" type="submit" className="btn-neon px-6 py-2.5 rounded-full text-sm">Conferma</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
